import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { INotebook, INote } from "../actions/notebooks";

export interface INotebookUIState {
    // Current selection
    currentNote: INote | null;

    // Modal states
    showCreateNotebookModal: boolean;
    showCreateNoteModal: boolean;
    showEditNotebookModal: boolean;
    showEditNoteModal: boolean;
    showDeleteModal: boolean;

    // Create notebook form
    newNotebookName: string;
    newNotebookDescription: string;
    newNotebookIcon: string;

    // Create note form
    newNoteName: string;
    newNoteDescription: string;

    // Edit notebook form
    editingNotebook: INotebook | null;
    editNotebookName: string;
    editNotebookDescription: string;
    editNotebookIcon: string;

    // Edit note form
    editingNote: INote | null;
    editNoteName: string;
    editNoteDescription: string;

    // Delete confirmation
    deleteItem: { type: "notebook" | "note"; item: INotebook | INote } | null;

    // Reload flag
    shouldReloadNotebooks: boolean;
}

const initialState: INotebookUIState = {
    currentNote: null,

    showCreateNotebookModal: false,
    showCreateNoteModal: false,
    showEditNotebookModal: false,
    showEditNoteModal: false,
    showDeleteModal: false,

    newNotebookName: "",
    newNotebookDescription: "",
    newNotebookIcon: "notebook",

    newNoteName: "",
    newNoteDescription: "",

    editingNotebook: null,
    editNotebookName: "",
    editNotebookDescription: "",
    editNotebookIcon: "",

    editingNote: null,
    editNoteName: "",
    editNoteDescription: "",

    deleteItem: null,

    shouldReloadNotebooks: false,
};

export const notebookUISlice = createSlice({
    name: "notebookUI",
    initialState,
    reducers: {
        // Current note selection
        setCurrentNote: (state, action: PayloadAction<INote | null>) => {
            state.currentNote = action.payload;
        },

        // Create notebook modal
        openCreateNotebookModal: (state) => {
            state.showCreateNotebookModal = true;
        },
        closeCreateNotebookModal: (state) => {
            state.showCreateNotebookModal = false;
            state.newNotebookName = "";
            state.newNotebookDescription = "";
            state.newNotebookIcon = "notebook";
        },
        setNewNotebookName: (state, action: PayloadAction<string>) => {
            state.newNotebookName = action.payload;
        },
        setNewNotebookDescription: (state, action: PayloadAction<string>) => {
            state.newNotebookDescription = action.payload;
        },
        setNewNotebookIcon: (state, action: PayloadAction<string>) => {
            state.newNotebookIcon = action.payload;
        },

        // Create note modal
        openCreateNoteModal: (state) => {
            state.showCreateNoteModal = true;
        },
        closeCreateNoteModal: (state) => {
            state.showCreateNoteModal = false;
            state.newNoteName = "";
            state.newNoteDescription = "";
        },
        setNewNoteName: (state, action: PayloadAction<string>) => {
            state.newNoteName = action.payload;
        },
        setNewNoteDescription: (state, action: PayloadAction<string>) => {
            state.newNoteDescription = action.payload;
        },

        // Edit notebook modal
        openEditNotebookModal: (state, action: PayloadAction<INotebook>) => {
            state.showEditNotebookModal = true;
            state.editingNotebook = action.payload;
            state.editNotebookName = action.payload.name;
            state.editNotebookDescription = action.payload.description || "";
            state.editNotebookIcon = action.payload.icon || "";
        },
        closeEditNotebookModal: (state) => {
            state.showEditNotebookModal = false;
            state.editingNotebook = null;
            state.editNotebookName = "";
            state.editNotebookDescription = "";
            state.editNotebookIcon = "";
        },
        setEditNotebookName: (state, action: PayloadAction<string>) => {
            state.editNotebookName = action.payload;
        },
        setEditNotebookDescription: (state, action: PayloadAction<string>) => {
            state.editNotebookDescription = action.payload;
        },
        setEditNotebookIcon: (state, action: PayloadAction<string>) => {
            state.editNotebookIcon = action.payload;
        },

        // Edit note modal
        openEditNoteModal: (state, action: PayloadAction<INote>) => {
            state.showEditNoteModal = true;
            state.editingNote = action.payload;
            state.editNoteName = action.payload.name;
            state.editNoteDescription = action.payload.description || "";
        },
        closeEditNoteModal: (state) => {
            state.showEditNoteModal = false;
            state.editingNote = null;
            state.editNoteName = "";
            state.editNoteDescription = "";
        },
        setEditNoteName: (state, action: PayloadAction<string>) => {
            state.editNoteName = action.payload;
        },
        setEditNoteDescription: (state, action: PayloadAction<string>) => {
            state.editNoteDescription = action.payload;
        },

        // Delete modal
        openDeleteModal: (
            state,
            action: PayloadAction<{
                type: "notebook" | "note";
                item: INotebook | INote;
            }>
        ) => {
            state.showDeleteModal = true;
            state.deleteItem = action.payload;
        },
        closeDeleteModal: (state) => {
            state.showDeleteModal = false;
            state.deleteItem = null;
        },

        // Reload flag
        setShouldReloadNotebooks: (state, action: PayloadAction<boolean>) => {
            state.shouldReloadNotebooks = action.payload;
        },

        // Helper action to clear current note when switching notebooks
        clearCurrentNoteOnNotebookSwitch: (state) => {
            state.currentNote = null;
        },

        // Auto-select first note logic
        autoSelectFirstNote: (
            state,
            action: PayloadAction<{
                notes: INote[];
                currentNotebook: string | null;
            }>
        ) => {
            const { notes, currentNotebook } = action.payload;
            const currentNotebookNotes = notes.filter(
                (note) => note.notebook_ui_id === currentNotebook
            );

            if (currentNotebookNotes.length > 0 && !state.currentNote) {
                state.currentNote = currentNotebookNotes[0];
            } else if (currentNotebookNotes.length === 0) {
                state.currentNote = null;
            } else if (
                state.currentNote &&
                !currentNotebookNotes.find(
                    (note) => note.ui_id === state.currentNote!.ui_id
                )
            ) {
                // If current note doesn't belong to this notebook, clear it
                state.currentNote = null;
            } else if (state.currentNote) {
                // Update the current note with latest data from notes array
                const updatedNote = currentNotebookNotes.find(
                    (note) => note.ui_id === state.currentNote!.ui_id
                );
                if (updatedNote) {
                    state.currentNote = updatedNote;
                }
            }
        },
    },
});

export const {
    setCurrentNote,
    openCreateNotebookModal,
    closeCreateNotebookModal,
    setNewNotebookName,
    setNewNotebookDescription,
    setNewNotebookIcon,
    openCreateNoteModal,
    closeCreateNoteModal,
    setNewNoteName,
    setNewNoteDescription,
    openEditNotebookModal,
    closeEditNotebookModal,
    setEditNotebookName,
    setEditNotebookDescription,
    setEditNotebookIcon,
    openEditNoteModal,
    closeEditNoteModal,
    setEditNoteName,
    setEditNoteDescription,
    openDeleteModal,
    closeDeleteModal,
    setShouldReloadNotebooks,
    clearCurrentNoteOnNotebookSwitch,
    autoSelectFirstNote,
} = notebookUISlice.actions;

export default notebookUISlice.reducer;
